"use client"

import { useState, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import "../styles/compra.css"
import CompraMapaCompartilhado from "./CompraMapaCompartilhado"

const API_URL = "https://api.ticketdovaqueiro.com"

const CompraPasso2Agente = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const {
    cpf,
    nome,
    apelido,
    categoriaSelecionada,
    evento,
    dadosPreenchidos,
  } = location.state || {}

  const [senhasSlots, setSenhasSlots] = useState([0])
  const [senhaAtiva, setSenhaAtiva] = useState(0)
  const [senhasEscolhidas, setSenhasEscolhidas] = useState({})
  const [quantidadeSolicitada, setQuantidadeSolicitada] = useState(1)
  const [modalQuantidadeVisible, setModalQuantidadeVisible] = useState(true)
  const [mapaCompartilhadoToken, setMapaCompartilhadoToken] = useState("")
  const [mapaCompartilhadoControlToken, setMapaCompartilhadoControlToken] = useState("")
  const [mapaCompartilhadoUrl, setMapaCompartilhadoUrl] = useState("")
  const [copiadoLink, setCopiadoLink] = useState(false)
  const ultimaSelecaoSincronizada = useRef("")
  const selecaoLocalPendente = useRef(false)

  const [diasEvento, setDiasEvento] = useState([])
  const [mapaSenhas, setMapaSenhas] = useState({})
  const [diaAberto, setDiaAberto] = useState(null)
  const [senhasReservadas, setSenhasReservadas] = useState([])
  const [capacidadeRestante, setCapacidadeRestante] = useState(null)

  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState("")

  const categoria = evento?.categorias?.find(c => c.nome === categoriaSelecionada)
  const maxSenhasPorPedido = categoria?.senhas?.length || 1
  const maxSenhas = capacidadeRestante !== null
    ? Math.max(0, Math.min(maxSenhasPorPedido, Number(capacidadeRestante) || 0))
    : maxSenhasPorPedido

  const slotsOrdenados = [...senhasSlots].sort((a, b) => a - b)
  const totalDefinidas = slotsOrdenados.filter(idx => !!senhasEscolhidas[idx]).length
  const todosPreenchidos = slotsOrdenados.length > 0 && slotsOrdenados.every(idx => !!senhasEscolhidas[idx])

  const estaReservada = (numero) =>
    senhasReservadas.some(r =>
      (typeof r === "object" ? Number(r.numero) : Number(r)) === Number(numero)
    )

  const abrirAviso = (mensagem) => {
    setModalMessage(mensagem)
    setModalVisible(true)
  }

  const carregarStatusSenhas = async ({ mostrarLoading = true, atualizarHora = false } = {}) => {
    if (!evento?._id || !categoriaSelecionada) return

    try {
      if (mostrarLoading) setLoading(true)
      if (atualizarHora) setRefreshing(true)

      const [resSenhas, resLimite] = await Promise.all([
        axios.get(
          `${API_URL}/api/pedidos/senhas-reservadas/${evento._id}/${encodeURIComponent(categoriaSelecionada)}`
        ),
        axios.get(
          `${API_URL}/api/pedidos/limite/${evento._id}/${encodeURIComponent(categoriaSelecionada)}`
        ).catch(() => ({ data: null })),
      ])

      const reservadas = Array.isArray(resSenhas.data) ? resSenhas.data : []
      setSenhasReservadas(reservadas)

      if (resLimite.data && resLimite.data.restantes !== null) {
        setCapacidadeRestante(resLimite.data.restantes)
      }

      if (atualizarHora) setUltimaAtualizacao(new Date())

      // Se alguma senha escolhida acabou ficando ocupada, remove da seleção.
      setSenhasEscolhidas(prev => {
        const novo = { ...prev }
        Object.entries(novo).forEach(([idx, senha]) => {
          const bloqueada = reservadas.some(r =>
            (typeof r === "object" ? Number(r.numero) : Number(r)) === Number(senha.numero)
          )
          if (bloqueada) delete novo[idx]
        })
        return novo
      })
    } catch (err) {
      console.error("Erro ao carregar senhas bloqueadas:", err)
      abrirAviso("Erro ao carregar mapa de senhas.")
    } finally {
      if (mostrarLoading) setLoading(false)
      if (atualizarHora) setRefreshing(false)
    }
  }

  useEffect(() => {
    const audio = new Audio("/audios/passo3.mp3")
    audio.play().catch(e => console.error("Erro ao reproduzir áudio:", e))
    return () => { audio.pause(); audio.currentTime = 0 }
  }, [])

  useEffect(() => {
    if (!evento || !categoriaSelecionada) return
    const cat = evento.categorias?.find(c => c.nome === categoriaSelecionada)
    if (cat?.dias?.length > 0) {
      setDiasEvento(cat.dias)
      setDiaAberto(cat.dias[0]?.nome || null)
    }
  }, [evento, categoriaSelecionada])

  useEffect(() => {
    if (diasEvento.length === 0) return

    const novoMapa = {}
    diasEvento.forEach((dia) => {
      const excluidas = dia.senhasExcluidas || []
      novoMapa[dia.nome] = Array.from(
        { length: dia.senhaFinal - dia.senhaInicial + 1 },
        (_, i) => dia.senhaInicial + i
      ).filter(n => !excluidas.includes(n))
    })
    setMapaSenhas(novoMapa)
  }, [diasEvento])

  useEffect(() => {
    carregarStatusSenhas({ mostrarLoading: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento?._id, categoriaSelecionada])

  const atualizarSenhas = () => {
    if (refreshing) return
    carregarStatusSenhas({ mostrarLoading: false, atualizarHora: true })
  }

  const criarLinkCompartilhado = async () => {
    try {
      if (mapaCompartilhadoToken && mapaCompartilhadoControlToken) {
        await axios.delete(`${API_URL}/api/pedidos/mapa-compartilhado/${mapaCompartilhadoToken}`, {
          data: { controlToken: mapaCompartilhadoControlToken },
        }).catch(() => {})
      }
      const selecoes = slotsOrdenados
        .filter((slot) => senhasEscolhidas[slot])
        .map((slot) => ({ slot, ...senhasEscolhidas[slot] }))
      const response = await axios.post(`${API_URL}/api/pedidos/mapa-compartilhado`, {
        eventoId: evento._id,
        categoria: categoriaSelecionada,
        quantidade: slotsOrdenados.length,
        selecoes,
      })
      const url = new URL(window.location.href)
      url.searchParams.set("mapaCompartilhado", response.data.token)
      const link = url.toString()
      setMapaCompartilhadoToken(response.data.token)
      setMapaCompartilhadoControlToken(response.data.controlToken)
      setMapaCompartilhadoUrl(link)
      ultimaSelecaoSincronizada.current = JSON.stringify(selecoes.sort((a, b) => a.slot - b.slot))
      try {
        await navigator.clipboard.writeText(link)
        setCopiadoLink(true)
        window.setTimeout(() => setCopiadoLink(false), 2500)
      } catch {
        // O link segue visível para cópia manual quando Clipboard API não estiver disponível.
      }
    } catch (err) {
      abrirAviso(err.response?.data?.mensagem || "Não foi possível criar o link compartilhado.")
    }
  }

  useEffect(() => {
    if (!mapaCompartilhadoToken) return
    let ativo = true
    const sincronizar = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/pedidos/mapa-compartilhado/${mapaCompartilhadoToken}`)
        if (!ativo || selecaoLocalPendente.current) return
        const selecoes = response.data?.sessao?.selecoes || []
        const assinatura = JSON.stringify(selecoes.map(({ slot, dia, numero }) => ({
          slot: Number(slot), dia, numero: Number(numero),
        })).sort((a, b) => a.slot - b.slot))
        if (assinatura !== ultimaSelecaoSincronizada.current) {
          ultimaSelecaoSincronizada.current = assinatura
          const escolhas = Object.fromEntries(selecoes.map(({ slot, dia, numero }) => [
            Number(slot), { dia, numero: Number(numero) },
          ]))
          setSenhasEscolhidas(escolhas)
          const vazio = slotsOrdenados.find((slot) => !escolhas[slot])
          setSenhaAtiva(vazio ?? slotsOrdenados[0] ?? 0)
        }
      } catch (err) {
        if (ativo && err.response?.status === 404) {
          setMapaCompartilhadoToken("")
          setMapaCompartilhadoControlToken("")
          setMapaCompartilhadoUrl("")
          abrirAviso("O link compartilhado expirou ou foi encerrado.")
        }
      }
    }
    sincronizar()
    const timer = window.setInterval(sincronizar, 1200)
    const timerStatus = window.setInterval(() => {
      carregarStatusSenhas({ mostrarLoading: false })
    }, 5000)
    return () => { ativo = false; window.clearInterval(timer); window.clearInterval(timerStatus) }
  }, [mapaCompartilhadoToken, slotsOrdenados.join(",")])

  useEffect(() => {
    if (!mapaCompartilhadoToken) return
    const selecoes = slotsOrdenados
      .filter((slot) => senhasEscolhidas[slot])
      .map((slot) => ({ slot, ...senhasEscolhidas[slot] }))
      .sort((a, b) => a.slot - b.slot)
    const assinatura = JSON.stringify(selecoes)
    if (assinatura === ultimaSelecaoSincronizada.current) return

    selecaoLocalPendente.current = true
    const timer = window.setTimeout(async () => {
      try {
        await axios.put(`${API_URL}/api/pedidos/mapa-compartilhado/${mapaCompartilhadoToken}`, { selecoes })
        ultimaSelecaoSincronizada.current = assinatura
      } catch (err) {
        if (err.response?.status === 409) {
          abrirAviso(err.response?.data?.mensagem || "Uma das senhas foi ocupada. Atualize o mapa e escolha outra.")
        }
      } finally {
        selecaoLocalPendente.current = false
      }
    }, 180)
    return () => window.clearTimeout(timer)
  }, [mapaCompartilhadoToken, senhasEscolhidas, slotsOrdenados.join(",")])

  const confirmarQuantidade = () => {
    const quantidade = Math.max(1, Math.min(maxSenhas, Number(quantidadeSolicitada) || 1))
    if (maxSenhas <= 0) {
      setModalQuantidadeVisible(false)
      abrirAviso("Não há senhas disponíveis nesta categoria no momento.")
      return
    }

    if (mapaCompartilhadoToken && quantidade !== slotsOrdenados.length) {
      axios.delete(`${API_URL}/api/pedidos/mapa-compartilhado/${mapaCompartilhadoToken}`, {
        data: { controlToken: mapaCompartilhadoControlToken },
      }).catch(() => {})
      setMapaCompartilhadoToken("")
      setMapaCompartilhadoControlToken("")
      setMapaCompartilhadoUrl("")
    }

    const novosSlots = Array.from({ length: quantidade }, (_, index) => index)
    setSenhasSlots(novosSlots)
    setSenhasEscolhidas(prev => Object.fromEntries(
      Object.entries(prev).filter(([idx]) => Number(idx) < quantidade)
    ))
    const primeiroVazio = novosSlots.find(idx => !senhasEscolhidas[idx])
    setSenhaAtiva(primeiroVazio ?? 0)
    setQuantidadeSolicitada(quantidade)
    setModalQuantidadeVisible(false)
  }

  const removerNumero = (event, slotIdx) => {
    event.stopPropagation()
    setSenhasEscolhidas(prev => {
      const novo = { ...prev }
      delete novo[slotIdx]
      return novo
    })
    setSenhaAtiva(slotIdx)
  }

  const selecionarNumero = (dia, numero) => {
    if (loading) return

    if (maxSenhas <= 0) {
      abrirAviso("Categoria esgotada no momento.")
      return
    }

    if (estaReservada(numero)) {
      abrirAviso("Esta senha já está reservada ou paga.")
      return
    }

    const jaEscolhidoPorOutroSlot = Object.entries(senhasEscolhidas).some(
      ([idx, senha]) => senha.numero === numero && senha.dia === dia && Number(idx) !== senhaAtiva
    )

    if (jaEscolhidoPorOutroSlot) {
      abrirAviso("Este número já foi escolhido para outra senha.")
      return
    }

    const novasEscolhidas = {
      ...senhasEscolhidas,
      [senhaAtiva]: { dia, numero },
    }

    setSenhasEscolhidas(novasEscolhidas)

    // Após cada escolha, ativa automaticamente a próxima senha ainda vazia.
    const proximoVazio = slotsOrdenados.find(slot => slot !== senhaAtiva && !novasEscolhidas[slot])
    if (proximoVazio !== undefined) setSenhaAtiva(proximoVazio)
  }

  const continuar = async () => {
    if (!todosPreenchidos) {
      abrirAviso("Selecione um número para cada senha antes de continuar.")
      return
    }

    const senhasSelecionadasNormalizadas = slotsOrdenados.map((_, index) => index)
    const senhasEscolhidasNormalizadas = slotsOrdenados.reduce((acc, slotIdx, index) => {
      acc[index] = senhasEscolhidas[slotIdx]
      return acc
    }, {})

    if (mapaCompartilhadoToken) {
      try {
        await axios.delete(`${API_URL}/api/pedidos/mapa-compartilhado/${mapaCompartilhadoToken}`, {
          data: { controlToken: mapaCompartilhadoControlToken },
        })
      } catch (err) {
        abrirAviso(err.response?.data?.mensagem || "Não foi possível encerrar o link do mapa. Verifique a conexão e tente novamente.")
        return
      }
      setMapaCompartilhadoToken("")
      setMapaCompartilhadoControlToken("")
    }

    navigate("/compra-passo-3", {
      state: {
        cpf,
        nome,
        apelido,
        categoriaSelecionada,
        evento,
        dadosPreenchidos,
        senhasSelecionadas: senhasSelecionadasNormalizadas,
        senhasEscolhidas: senhasEscolhidasNormalizadas,
        senhasDefinidasPeloComprador: Boolean(mapaCompartilhadoToken),
      }
    })
  }

  const diaAtual = diasEvento.find(dia => dia.nome === diaAberto)
  const numerosDiaAtual = diaAtual ? mapaSenhas[diaAtual.nome] || [] : []

  const textoAtualizacao = ultimaAtualizacao
    ? `Atualizado às ${ultimaAtualizacao.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
    : "Toque em ↻ para atualizar o mapa"

  if (!evento || !categoriaSelecionada) {
    return (
      <main className="compra-container3 compra-passo2-app">
        <div className="app-empty-state">
          <strong>Não foi possível abrir o mapa.</strong>
          <span>Volte para o passo anterior e escolha a categoria novamente.</span>
          <button type="button" onClick={() => navigate(-1)}>Voltar</button>
        </div>
      </main>
    )
  }

  return (
    <main className="compra-container3 compra-passo2-app">
      <header className="app-map-header">
        <button type="button" className="app-back-btn" onClick={() => navigate(-1)} aria-label="Voltar">
          ←
        </button>
        <h1>Passo 2: Escolha sua Senha</h1>
        <button
          type="button"
          className={`app-refresh-btn ${refreshing ? "girando" : ""}`}
          onClick={atualizarSenhas}
          disabled={refreshing}
          aria-label="Atualizar mapa"
        >
          ↻
        </button>
      </header>

      <section className="app-slots-area" aria-label="Senhas selecionadas">
        <div className="app-selection-heading">
          <div>
            <strong>Suas senhas</strong>
            <small>{totalDefinidas} de {slotsOrdenados.length} escolhidas · toque em um cartão para trocar</small>
          </div>
          <button type="button" className="app-quantity-edit" onClick={() => {
            setQuantidadeSolicitada(slotsOrdenados.length)
            setModalQuantidadeVisible(true)
          }}>Alterar quantidade</button>
        </div>
        <div className="app-slots-scroll">
          {slotsOrdenados.map((slotIdx) => {
            const escolhida = senhasEscolhidas[slotIdx]
            const ativo = senhaAtiva === slotIdx
            const numeroVisual = slotsOrdenados.indexOf(slotIdx) + 1

            return (
              <button
                type="button"
                key={slotIdx}
                className={`app-slot-card ${ativo ? "ativo" : ""} ${escolhida ? "preenchido" : ""}`}
                onClick={() => setSenhaAtiva(slotIdx)}
                aria-pressed={ativo}
              >
                <span className="app-slot-label">Senha {numeroVisual}</span>

                {escolhida ? (
                  <>
                    <strong className="app-slot-numero">{escolhida.numero}</strong>
                    <small>{escolhida.dia}</small>
                    <span
                      className="app-slot-remove"
                      role="button"
                      tabIndex={0}
                      onClick={(event) => removerNumero(event, slotIdx)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") removerNumero(event, slotIdx)
                      }}
                      aria-label="Remover número escolhido"
                    >
                      ×
                    </span>
                  </>
                ) : (
                  <>
                    <em>{ativo ? "escolha no mapa" : "aguardando"}</em>
                  </>
                )}
              </button>
            )
          })}
        </div>
        <div className="app-shared-link-panel">
          <div>
            <strong>Cliente escolhe pelo próprio celular</strong>
            <small>As escolhas aparecem nesta tela automaticamente.</small>
          </div>
          <button type="button" onClick={criarLinkCompartilhado}>
            {mapaCompartilhadoUrl ? (copiadoLink ? "LINK COPIADO ✓" : "GERAR OUTRO LINK") : "CRIAR E COPIAR LINK"}
          </button>
          {mapaCompartilhadoUrl && (
            <div className="app-shared-link-value">
              <input aria-label="Link compartilhado" readOnly value={mapaCompartilhadoUrl} onFocus={(event) => event.target.select()} />
              <button type="button" onClick={async () => {
                try { await navigator.clipboard.writeText(mapaCompartilhadoUrl); setCopiadoLink(true) }
                catch { abrirAviso("Selecione e copie o link exibido.") }
              }}>COPIAR</button>
              <small>Link válido por 1 hora. O cliente só consegue escolher os números.</small>
            </div>
          )}
        </div>
      </section>

      <nav className="app-day-tabs" aria-label="Dias do evento">
        {diasEvento.map((dia) => (
          <button
            type="button"
            key={dia.nome}
            className={`app-day-tab ${diaAberto === dia.nome ? "ativo" : ""}`}
            onClick={() => setDiaAberto(dia.nome)}
          >
            {dia.nome}
          </button>
        ))}
      </nav>

      <div className="app-update-row">
        <span>◷</span>
        <small>{textoAtualizacao}</small>
      </div>

      <section className="app-map-area" aria-label="Mapa de senhas">
        {loading ? (
          <div className="app-loading-map">
            <div className="loading-spinner" />
            <span>Carregando mapa de senhas...</span>
          </div>
        ) : diaAtual ? (
          <div className="app-number-grid">
            {numerosDiaAtual.map((numero) => {
              const reservada = estaReservada(numero)
              const selecionadaSlotAtivo = senhasEscolhidas[senhaAtiva]?.numero === numero &&
                senhasEscolhidas[senhaAtiva]?.dia === diaAtual.nome
              const selecionadaOutroSlot = Object.entries(senhasEscolhidas).some(
                ([idx, senha]) => senha.numero === numero && senha.dia === diaAtual.nome && Number(idx) !== senhaAtiva
              )

              return (
                <button
                  type="button"
                  key={numero}
                  className={`app-number-box ${reservada ? "ocupada" : "livre"} ${selecionadaSlotAtivo ? "sua-escolha" : ""} ${selecionadaOutroSlot ? "outra-senha" : ""}`}
                  onClick={() => selecionarNumero(diaAtual.nome, numero)}
                  disabled={reservada}
                  aria-pressed={selecionadaSlotAtivo || selecionadaOutroSlot}
                >
                  {numero}
                </button>
              )
            })}
          </div>
        ) : (
          <div className="app-empty-map">
            Nenhum dia de mapa encontrado para esta categoria.
          </div>
        )}
      </section>

      <section className="app-legend-bar" aria-label="Legenda do mapa">
        <span><i className="livre" />Livre</span>
        <span><i className="ocupada" />Ocupada</span>
        <span><i className="sua" />Sua Escolha</span>
        <span><i className="outra" />Outra Senha</span>
      </section>

      <footer className="app-bottom-bar">
        <div>
          <strong>{slotsOrdenados.length} senha(s) selecionada(s)</strong>
          <small>{totalDefinidas} de {slotsOrdenados.length} definidas</small>
        </div>
        <button
          type="button"
          className="app-next-btn"
          disabled={!todosPreenchidos || loading}
          onClick={continuar}
        >
          DADOS E OPCIONAIS <span>→</span>
        </button>
      </footer>

      {modalVisible && (
        <div className="aviso-modal app-modal" role="dialog" aria-modal="true">
          <div className="aviso-conteudo app-modal-content">
            <div className="app-modal-icon">!</div>
            <h1>Atenção</h1>
            <p>{modalMessage}</p>
            <button type="button" className="btn-entendi" onClick={() => setModalVisible(false)}>
              ENTENDI
            </button>
          </div>
        </div>
      )}

      {modalQuantidadeVisible && (
        <div className="app-quantity-backdrop" role="dialog" aria-modal="true" aria-labelledby="app-quantity-title">
          <div className="app-quantity-modal">
            <span className="app-quantity-eyebrow">COMPRA EM UM SÓ PEDIDO</span>
            <h2 id="app-quantity-title">Quantas senhas você quer?</h2>
            <p>Escolha a quantidade agora. Depois, selecione todos os números no mapa sem precisar voltar para adicionar um por um.</p>
            <div className="app-quantity-stepper" aria-label="Quantidade de senhas">
              <button type="button" aria-label="Diminuir quantidade" disabled={quantidadeSolicitada <= 1}
                onClick={() => setQuantidadeSolicitada(q => Math.max(1, Number(q) - 1))}>−</button>
              <strong>{quantidadeSolicitada}</strong>
              <button type="button" aria-label="Aumentar quantidade" disabled={quantidadeSolicitada >= maxSenhas}
                onClick={() => setQuantidadeSolicitada(q => Math.min(maxSenhas, Number(q) + 1))}>+</button>
            </div>
            <small className="app-quantity-limit">Limite disponível: {maxSenhas} {maxSenhas === 1 ? "senha" : "senhas"}</small>
            <button type="button" className="app-quantity-confirm" disabled={maxSenhas <= 0}
              onClick={confirmarQuantidade}>ESCOLHER NÚMEROS <span>→</span></button>
            <button type="button" className="app-quantity-cancel" onClick={() => {
              if (slotsOrdenados.length > 0 && totalDefinidas > 0) setModalQuantidadeVisible(false)
              else navigate(-1)
            }}>Voltar</button>
          </div>
        </div>
      )}
    </main>
  )
}

const CompraPasso2 = () => {
  const location = useLocation()
  const token = new URLSearchParams(location.search).get("mapaCompartilhado")
  return token ? <CompraMapaCompartilhado token={token} /> : <CompraPasso2Agente />
}

export default CompraPasso2
